import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
    });

    // Update axios headers when token changes
    useEffect(() => {
        if (token) {
            api.defaults.headers.Authorization = `Bearer ${token}`;
        } else {
            delete api.defaults.headers.Authorization;
        }
    }, [token, api]);

    // Load user on mount
    useEffect(() => {
        const loadUser = async () => {
            if (token) {
                try {
                    const response = await api.get('/auth/me');
                    setUser(response.data);
                } catch (error) {
                    console.error('Failed to load user:', error);
                    localStorage.removeItem('token');
                    setToken(null);
                }
            }
            setLoading(false);
        };
        loadUser();
    }, []);

    const login = useCallback(async (email, password) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('token', access_token);
            setToken(access_token);
            setUser(userData);
            
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Login failed' 
            };
        }
    }, []);

    const register = useCallback(async (email, password, name, birth_year = null) => {
        try {
            const payload = { email, password, name };
            if (birth_year) payload.birth_year = birth_year;
            
            const response = await axios.post(`${API_URL}/api/auth/register`, payload);
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('token', access_token);
            setToken(access_token);
            setUser(userData);
            
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Registration failed' 
            };
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    }, []);

    const updateProfile = useCallback(async (data) => {
        try {
            const response = await api.put('/user/profile', data);
            setUser(response.data);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: error.response?.data?.detail || 'Update failed' 
            };
        }
    }, [api]);

    const value = {
        user,
        token,
        loading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        updateProfile,
        api
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
